/**
 * The SQL editor's `SELECT *` hover provider (Monaco): parses the buffer with
 * the Visual Query Editor's parse options, finds the `*` select item under the
 * pointer (`findSelectStarAt`) and lists its columns against the schema
 * snapshot the VQE reads (`buildLivePullSchemaSnapshot`), so the hover and
 * "Choose columns" never disagree (`selectStarExpansion.ts`).
 *
 * Kept cheap for repeated hovers:
 *   - Nothing runs unless the character under the pointer is `*`.
 *   - The parsed buffer is kept per editor for the model's current version.
 *   - The pulled snapshot is shared across editors per (connection, engine,
 *     database) and dropped on the same schema-bus events the VQE re-pulls
 *     on (`updated` for that database, `invalidated`, `disconnected`); the
 *     last few pairs are kept.
 */

import type * as monacoNs from 'monaco-editor';
import type { editor as monacoEditor } from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';
import { subscribe as subscribeSchemaBus } from '@/features/intellisense/schemaBus/schemaBus';
import { buildParsedDocumentFromSource, getDocumentStructure } from '@/features/sql-grammar/document';
import type { ParseOptions, SqlEngine } from '@/features/sql-grammar/model';
import type { ParsedBuffer, SchemaSnapshot } from '@/features/sql-resolver/types';
import { databaseEngineIdToSqlEngine } from '@/core/engines/databaseEngineIdToSqlEngine';
import { buildLivePullSchemaSnapshot } from '@/features/workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { vqeParseOptionsFor } from '@/features/workspace/visual-query-editor/workspace/classifyTabQuery';

import { readModelTextSafe } from './monacoModelText';
import { findSelectStarAt, selectStarHoverMarkdown } from './selectStarExpansion';

export type PullHoverSnapshot = (connectionId: string, engine: SqlEngine, database: string) => Promise<SchemaSnapshot | undefined>;

export type SelectStarHoverDeps = {
  /** The model the editor shows — other models (diff / peek) get no hover. */
  readonly isEditorModel: (model: monacoEditor.ITextModel) => boolean;
  readonly getEngineId: () => DatabaseEngineId;
  readonly getConnectionId: () => string | null | undefined;
  readonly getDatabase: () => string | null | undefined;
  /** Test seam; defaults to the VQE's live pull. */
  readonly pullSnapshot?: PullHoverSnapshot;
};

const livePull: PullHoverSnapshot = (connectionId, engine, database) =>
  buildLivePullSchemaSnapshot(connectionId, engine)(database);

const parseOptionsByEngine = new Map<DatabaseEngineId, ParseOptions | null>();

/**
 * The VQE's parse options for the editor's engine (one object per engine, so
 * the parse cache can compare them); null for Kusto. The mock engine reads the
 * PostgreSQL cache, as its tests seed it.
 */
export function hoverParseOptions(engineId: DatabaseEngineId): ParseOptions | null {
  let options = parseOptionsByEngine.get(engineId);
  if (options === undefined) {
    const sqlEngineId = engineId === 'mock' ? 'pgsql' : engineId;
    options = databaseEngineIdToSqlEngine(sqlEngineId) ? vqeParseOptionsFor(sqlEngineId) : null;
    parseOptionsByEngine.set(engineId, options);
  }
  return options;
}

export function createSelectStarHoverProvider(deps: SelectStarHoverDeps): monacoNs.languages.HoverProvider {
  let parsed: { readonly model: monacoEditor.ITextModel; readonly version: number; readonly options: ParseOptions; readonly buffer: ParsedBuffer } | null = null;

  const parse = (model: monacoEditor.ITextModel, options: ParseOptions): ParsedBuffer | null => {
    const version = model.getVersionId();
    if (parsed?.model === model && parsed.version === version && parsed.options === options) return parsed.buffer;
    const source = readModelTextSafe(model);
    if (source === null) return null;
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const buffer: ParsedBuffer = { parsedDocument, documentStructure: getDocumentStructure(parsedDocument, 'select-star-hover') };
    parsed = { model, version, options, buffer };
    return buffer;
  };

  return {
    provideHover: async (model, position, token) => {
      if (!deps.isEditorModel(model)) return null;
      const { lineNumber, column } = position;
      const range = { startLineNumber: lineNumber, startColumn: column, endLineNumber: lineNumber, endColumn: column + 1 };
      if (model.getValueInRange(range) !== '*') return null;
      const connectionId = deps.getConnectionId();
      const database = deps.getDatabase();
      const options = hoverParseOptions(deps.getEngineId());
      if (!connectionId || !database || !options) return null;
      const version = model.getVersionId();
      let markdown: string | null;
      try {
        const buffer = parse(model, options);
        const target = buffer ? findSelectStarAt(buffer, model.getOffsetAt(position)) : null;
        if (!target) return null;
        const snapshot = await hoverSchemaSnapshot(connectionId, options.engine, database, deps.pullSnapshot);
        if (token.isCancellationRequested || model.isDisposed() || model.getVersionId() !== version) return null;
        markdown = selectStarHoverMarkdown(target, options, snapshot);
      } catch {
        return null;
      }
      return markdown ? { contents: [{ value: markdown, isTrusted: true }], range } : null;
    },
  };
}

type SnapshotEntry = { readonly promise: Promise<SchemaSnapshot | undefined>; readonly unsubscribe: () => void };

const MAX_SNAPSHOT_ENTRIES = 4;
const snapshotEntries = new Map<string, SnapshotEntry>();

function dropSnapshot(key: string, entry?: SnapshotEntry): void {
  const held = snapshotEntries.get(key);
  if (!held || (entry && held !== entry)) return;
  snapshotEntries.delete(key);
  held.unsubscribe();
}

/** The live schema snapshot for the hover, pulled once and reused until the schema bus says it changed. */
export function hoverSchemaSnapshot(
  connectionId: string,
  engine: SqlEngine,
  database: string,
  pull: PullHoverSnapshot = livePull,
): Promise<SchemaSnapshot | undefined> {
  const key = `${connectionId}|${engine}|${database}`;
  const hit = snapshotEntries.get(key);
  if (hit) {
    snapshotEntries.delete(key);
    snapshotEntries.set(key, hit);
    return hit.promise;
  }
  const entry: SnapshotEntry = {
    promise: pull(connectionId, engine, database),
    // The bus never emits synchronously from `subscribe`, so `entry` is set by the first event.
    unsubscribe: subscribeSchemaBus(connectionId, (event) => {
      if (event.type === 'warmRequested' || (event.type === 'updated' && event.database !== database)) return;
      dropSnapshot(key, entry);
    }),
  };
  snapshotEntries.set(key, entry);
  entry.promise.catch(() => dropSnapshot(key, entry));
  for (const oldest of snapshotEntries.keys()) {
    if (snapshotEntries.size <= MAX_SNAPSHOT_ENTRIES) break;
    dropSnapshot(oldest);
  }
  return entry.promise;
}

/** Test seam — forget every pulled snapshot. */
export function __resetSelectStarHoverSnapshotsForTest(): void {
  for (const key of [...snapshotEntries.keys()]) dropSnapshot(key);
}
